"use client";

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@clarityos/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@clarityos/ui/dialog";
import { formatCurrency } from "@/lib/currency";
import { formatDate } from "@/lib/time";
import type { PaymentReceivedNotice } from "../hooks/usePaymentReceivedNotice";
import { useTenantRouteContext } from "@/components/app-shell/TenantRouteContext";
import {
  statementHistoryQueryKey,
  useStatementHistory,
} from "../queries/useStatementHistory";
import { findNewPayment } from "../utils/paymentMatching";
import { StatementStatusBadge } from "./StatementStatusBadge";

interface PaymentReceivedDialogProps {
  patientId: string | null;
  notice: PaymentReceivedNotice | null;
  onDismiss: VoidFunction;
}

const PAYMENT_RETRY_DELAYS_MS = [2000, 5000, 10000];

export function PaymentReceivedDialog({
  patientId,
  notice,
  onDismiss,
}: PaymentReceivedDialogProps) {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantRouteContext();
  const { data: history } = useStatementHistory(notice ? patientId : null);
  const payment = notice ? findNewPayment(history, notice) : undefined;
  const hasPayment = payment !== undefined;
  const isOpen = notice !== null;
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen || hasPayment || !patientId) return;

    const timers = PAYMENT_RETRY_DELAYS_MS.map((delay) =>
      setTimeout(
        () =>
          void queryClient.invalidateQueries(
            { queryKey: statementHistoryQueryKey(tenantId, patientId) },
            { cancelRefetch: false },
          ),
        delay,
      ),
    );
    return () => timers.forEach(clearTimeout);
  }, [isOpen, hasPayment, notice, patientId, tenantId, queryClient]);

  const statement = payment?.statement;
  const received = payment?.amount;

  return (
    <Dialog open={!!notice} onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent
        size="sm"
        showCloseButton={false}
        className="flex flex-col items-center gap-4 text-center"
        onOpenAutoFocus={() => {
          previouslyFocused.current = document.activeElement as HTMLElement;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          const previous = previouslyFocused.current;
          const target = previous?.isConnected
            ? previous
            : document.querySelector<HTMLElement>('[role="dialog"]');
          target?.focus();
        }}
      >
        <div className="flex size-12 items-center justify-center rounded-full bg-[#E8F7EE] text-[#1F9D55]">
          <CheckCircle2 className="size-6" />
        </div>
        <DialogHeader className="items-center text-center">
          <DialogTitle>Payment received</DialogTitle>
          <DialogDescription>
            {received != null
              ? `The patient just paid ${formatCurrency(received)}.`
              : "The patient just completed a payment."}
          </DialogDescription>
        </DialogHeader>
        {statement && (
          <div className="grid w-full grid-cols-2 gap-2 text-left text-sm">
            <div className="rounded-lg bg-[#F9FAFB] p-3">
              <p className="text-xs text-[#6B7280]">Statement</p>
              <p>{statement.sentDate ? formatDate(statement.sentDate) : "—"}</p>
            </div>
            <div className="rounded-lg bg-[#F9FAFB] p-3">
              <p className="text-xs text-[#6B7280]">Status</p>
              {statement.paymentStatus && (
                <StatementStatusBadge status={statement.paymentStatus} />
              )}
            </div>
          </div>
        )}
        <DialogFooter className="w-full pt-0">
          <Button className="flex-1 rounded-full" onClick={onDismiss}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
